import { createContext, useContext } from 'react';
import { RouteStore } from 'stores/RouteStore';
import { DiligencePageStore } from 'stores/diligence/DiligencePageStore';
import { RequestAccessPageStore } from 'stores/diligence/RequestAccessPageStore';
import { AuthEvent, AuthStore } from 'stores/users/AuthStore';
import { UserListStore } from 'stores/users/UserListStore';
import { CreateUserStore } from 'stores/users/CreateUserStore';
import { ChangePasswordStore } from 'stores/users/ChangePasswordStore';
import { EditUserStore } from 'stores/users/EditUserStore';
import { makeAutoObservable } from 'mobx';
import { RegisterUserStore } from 'stores/users/RegisterUserStore';
import { getFeatureFlagValue } from '@bowery-valuation/feature-flagger-client';
import { featureFlags } from 'utils/config';
import { ConfirmEmailStore } from 'stores/users/ConfirmEmailStore';
import { UserProfileStore } from 'stores/users/UserProfileStore';
import { ResetPasswordStore } from 'stores/users/ResetPasswordStore';
import { ResetPasswordEmailStore } from 'stores/users/ResetPasswordEmailStore';
import { CompleteProfileStore } from 'stores/users/CompleteProfileStore';
import { AccountsStore } from 'stores/users/AccountsStore';
import { SocialAuthStore } from 'stores/users/SocialAuthStore';
import { LoginStore } from 'stores/users/LoginStore';
import { DeleteUserStore } from 'stores/users/DeleteUserStore';
import { JobsViewStore } from 'stores/jobs/JobsViewStore';
import { JobsListStore } from 'stores/jobs/JobsListStore';
import { NotificationSettingsStore } from './users/NotificationSettingsStore';
import { UnderwritingDashboardPageStore } from '../components/underwriting/stores/UnderwritingDashboardPageStore';
import { UnderwritingPageStore } from './underwriting/UnderwritingPageStore';
import { ExpenseCompsStore } from 'components/underwriting/income-approach/expenses/comparable-expenses/search-modal/ExpenseCompsStore';

export class RootStore {
  routeStore: RouteStore;
  authStore: AuthStore;
  socialAuthStore: SocialAuthStore;
  loginStore: LoginStore;
  registerUserStore!: RegisterUserStore;
  resetPasswordStore: ResetPasswordStore;
  resetPasswordEmailStore: ResetPasswordEmailStore;
  diligencePageStore: DiligencePageStore;
  requestAccessPageStore: RequestAccessPageStore;
  createUserStore!: CreateUserStore;
  editUserStore!: EditUserStore;
  userListStore!: UserListStore;
  changePasswordStore!: ChangePasswordStore;
  deleteUserStore!: DeleteUserStore;
  completeProfileStore!: CompleteProfileStore;
  userProfileStore!: UserProfileStore;
  confirmEmailStore!: ConfirmEmailStore;
  accountsStore!: AccountsStore;
  jobsViewStore!: JobsViewStore;
  jobsListStore!: JobsListStore;
  notificationSettingsStore!: NotificationSettingsStore;
  underwritingDashboardPageStore!: UnderwritingDashboardPageStore;
  underwritingPageStore!: UnderwritingPageStore;
  expenseCompsStore!: ExpenseCompsStore;
  userRegistration: boolean = false;

  constructor() {
    this.routeStore = new RouteStore(this);

    this.authStore = new AuthStore();
    this.socialAuthStore = new SocialAuthStore(this.authStore);
    this.confirmEmailStore = new ConfirmEmailStore(this.authStore, this.routeStore);
    this.loginStore = new LoginStore(this.authStore, this.socialAuthStore, this.confirmEmailStore);
    this.registerUserStore = new RegisterUserStore(this.authStore, this.socialAuthStore, this.confirmEmailStore);
    this.resetPasswordEmailStore = new ResetPasswordEmailStore();
    this.resetPasswordStore = new ResetPasswordStore();
    this.jobsListStore = new JobsListStore(this.authStore);
    this.diligencePageStore = new DiligencePageStore(this.authStore, this.jobsListStore, this.routeStore);
    this.requestAccessPageStore = new RequestAccessPageStore(this.authStore, this.routeStore);
    this.initAuthenticatedStores();

    makeAutoObservable(this, {}, { autoBind: true });

    this.userRegistration = getFeatureFlagValue(featureFlags.userRegistration);
  }

  async init() {
    await this.authStore.checkAuth();
    this.routeStore.init(this.authStore.status === 'invalid' ? window.location.pathname : undefined);

    this.authStore.events.addEventListener(AuthEvent.eventType, (event) => {
      if (event instanceof AuthEvent) {
        if (event.action === 'login') {
          this.initAuthenticatedStores();
          // if you log in from the diligence page, and the new login UI is not enabled yet, stay there
          if (!this.userRegistration && this.routeStore.route === 'diligence') return;
          this.routeStore.goToRedirectUrl();
        } else if (event.action === 'forcedLogout' || event.action === 'manualLogout') {
          // if you log out from the diligence page, and the new login UI is not enabled yet, stay there
          if (!this.userRegistration && this.routeStore.route === 'diligence') return;
          this.routeStore.navigateByName('login');
        }
      }
    });
  }

  private initAuthenticatedStores() {
    this.accountsStore = new AccountsStore(this.authStore);
    this.createUserStore = new CreateUserStore(this.authStore);
    this.userListStore = new UserListStore(this.authStore);
    this.editUserStore = new EditUserStore(this.authStore, this.userListStore, this.routeStore);
    this.changePasswordStore = new ChangePasswordStore(this.authStore);
    this.deleteUserStore = new DeleteUserStore(this.authStore);
    this.completeProfileStore = new CompleteProfileStore(this.authStore, this.routeStore, this.accountsStore);
    this.userProfileStore = new UserProfileStore(this.authStore, this.accountsStore);
    this.jobsViewStore = new JobsViewStore(this.jobsListStore);
    this.notificationSettingsStore = new NotificationSettingsStore(this.authStore);
    this.underwritingDashboardPageStore = new UnderwritingDashboardPageStore(this.authStore);
    this.underwritingPageStore = new UnderwritingPageStore(this.authStore);
    this.expenseCompsStore = new ExpenseCompsStore();
  }
}

export const RootStoreContext = createContext<RootStore>({} as RootStore);

export const useRootStore = (): RootStore => useContext(RootStoreContext);
